/**
 * Where the visitor was before they opened /contact. (BL-0096)
 *
 * The obvious answer, `document.referrer`, is wrong here twice over. Volto is
 * a single-page app, so after any in-app navigation the referrer still names
 * the page the visitor first ENTERED on, which may be many clicks ago. And the
 * public edge sends `Referrer-Policy: " "` — an invalid value, which browsers
 * treat as no policy at all and fall back to `strict-origin-when-cross-origin`,
 * so a cross-origin referrer arrives as a bare origin with no path.
 *
 * So the trail is kept explicitly: the last path this app rendered before the
 * current one. sessionStorage rather than a module variable, because /contact
 * is often reached by a full page load (a link in an email, a bookmark, or an
 * SSR entry) and a module variable does not survive that. Per-tab, cleared
 * when the tab closes, never sent anywhere except inside a submission the
 * visitor chose to make.
 *
 * A prefill link's own `?path=` always wins over this — an explicit statement
 * beats an inference.
 */
const KEY = 'pp_route_trail';

/** Paths that are never a useful "came from": the contact page itself, and
 *  anything under the CMS. */
const IGNORED = /^\/(contact|login|logout|register|password-reset|\+\+api\+\+)/;

const read = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
};

const write = (value) => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* private mode, storage disabled, quota: the trail is a nicety */
  }
};

/** Record a rendered route. Safe to call on every navigation. */
export function recordRoute(pathname) {
  if (typeof window === 'undefined' || !pathname) return;
  if (IGNORED.test(pathname)) return;
  const trail = read();
  if (trail.current === pathname) return;
  write({ previous: trail.current || '', current: pathname });
}

/** The route before the current one, or '' when there is nothing to say. */
export function previousRoute() {
  if (typeof window === 'undefined') return '';
  return read().previous || '';
}
